import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { Document, Page } from 'react-pdf'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    FIELD_DEFINITIONS,
    FIELD_GROUP_LABELS,
    FIELD_GROUP_ORDER,
    FIELD_TYPE_LABELS,
    TemplateField,
    TemplateFieldType,
    hasSignatureField,
    parseFields,
    templateRequirements,
} from './types'

const PAGE_RENDER_WIDTH = 760
const MIN_BOX_SIZE = 0.02

// Default box sizes per field type (fractions of the page).
const DEFAULT_SIZES: Record<'text' | 'wide' | 'signature', { w: number; h: number }> = {
    text: { w: 0.25, h: 0.03 },
    wide: { w: 0.5, h: 0.03 },
    signature: { w: 0.25, h: 0.07 },
}

const sizeKeyFor = (type: TemplateFieldType): keyof typeof DEFAULT_SIZES => {
    if (type === 'signature') return 'signature'
    if (type === 'full_address' || type === 'address_line_1') return 'wide'
    return 'text'
}

const PALETTE_GROUPS = FIELD_GROUP_ORDER.map((group) => ({
    group,
    label: FIELD_GROUP_LABELS[group],
    types: FIELD_DEFINITIONS.filter((d) => d.group === group).map((d) => d.type),
}))

interface DragState {
    id: string
    page: number
    mode: 'move' | 'resize'
    offsetX: number
    offsetY: number
}

interface FieldEditorProps {
    templateId: number
    templateName: string
    onClose: () => void
    onSaved: (fields: TemplateField[]) => void
}

export const FieldEditor = ({ templateId, templateName, onClose, onSaved }: FieldEditorProps) => {
    const [pdfBase64, setPdfBase64] = useState<string | null>(null)
    const [numPages, setNumPages] = useState(0)
    const [fields, setFields] = useState<TemplateField[]>([])
    const [activeType, setActiveType] = useState<TemplateFieldType>('name')

    const dragStateRef = useRef<DragState | null>(null)
    const didDragRef = useRef(false)
    const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())

    // react-pdf re-fetches when the `file` prop identity changes; keep it stable.
    const documentFile = useMemo(() => (pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null), [pdfBase64])

    useEffect(() => {
        const load = async () => {
            Loading.circle('Loading template...')
            try {
                const response = await api({ url: 'contract-sign/templates/get-doc', data: { template_id: templateId } })
                if (!response.data?.success) {
                    Report.failure('Error', response.data?.message || 'Could not load the template.', 'Okay')
                    onClose()
                    return
                }
                setPdfBase64(response.data.pdf_base64)
                setFields(parseFields(response.data.fields))
            } catch (error) {
                console.error('Unable to load template doc:', error)
                Report.failure('Error', 'Could not load the template document.', 'Okay')
                onClose()
            } finally {
                Loading.remove()
            }
        }
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId])

    const registerPageRef = (pageNumber: number) => (el: HTMLDivElement | null) => {
        if (el) pageRefs.current.set(pageNumber, el)
        else pageRefs.current.delete(pageNumber)
    }

    const addField = (pageNumber: number, e: React.MouseEvent<HTMLDivElement>) => {
        // A drag that ends over the overlay also fires click — ignore it.
        if (didDragRef.current) {
            didDragRef.current = false
            return
        }
        if (e.target !== e.currentTarget) return
        const size = DEFAULT_SIZES[sizeKeyFor(activeType)]
        const rect = e.currentTarget.getBoundingClientRect()
        const clickX = (e.clientX - rect.left) / rect.width
        const clickY = (e.clientY - rect.top) / rect.height
        const x = Math.min(Math.max(clickX - size.w / 2, 0), 1 - size.w)
        const y = Math.min(Math.max(clickY - size.h / 2, 0), 1 - size.h)
        setFields((prev) => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                type: activeType,
                label: FIELD_TYPE_LABELS[activeType],
                page: pageNumber,
                x,
                y,
                w: size.w,
                h: size.h,
            },
        ])
    }

    const removeField = (id: string) => setFields((prev) => prev.filter((f) => f.id !== id))

    const updateLabel = (id: string, label: string) =>
        setFields((prev) => prev.map((f) => (f.id === id ? { ...f, label } : f)))

    const startDrag = (field: TemplateField, mode: 'move' | 'resize') => (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const pageEl = pageRefs.current.get(field.page)
        if (!pageEl) return
        const rect = pageEl.getBoundingClientRect()
        const pointerX = (e.clientX - rect.left) / rect.width
        const pointerY = (e.clientY - rect.top) / rect.height
        dragStateRef.current = {
            id: field.id,
            page: field.page,
            mode,
            offsetX: pointerX - field.x,
            offsetY: pointerY - field.y,
        }
        didDragRef.current = false
    }

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            const drag = dragStateRef.current
            if (!drag) return
            const pageEl = pageRefs.current.get(drag.page)
            if (!pageEl) return
            didDragRef.current = true
            const rect = pageEl.getBoundingClientRect()
            const pointerX = (e.clientX - rect.left) / rect.width
            const pointerY = (e.clientY - rect.top) / rect.height

            setFields((prev) =>
                prev.map((f) => {
                    if (f.id !== drag.id) return f
                    if (drag.mode === 'move') {
                        const x = Math.min(Math.max(pointerX - drag.offsetX, 0), 1 - f.w)
                        const y = Math.min(Math.max(pointerY - drag.offsetY, 0), 1 - f.h)
                        return { ...f, x, y }
                    }
                    const w = Math.min(Math.max(pointerX - f.x, MIN_BOX_SIZE), 1 - f.x)
                    const h = Math.min(Math.max(pointerY - f.y, MIN_BOX_SIZE), 1 - f.y)
                    return { ...f, w, h }
                })
            )
        }
        const onMouseUp = () => {
            dragStateRef.current = null
            // Leave didDragRef set so the click that follows a drag is ignored,
            // then clear it on the next tick.
            window.setTimeout(() => {
                didDragRef.current = false
            }, 0)
        }
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [])

    const saveFields = async () => {
        Loading.circle('Saving field layout...')
        try {
            const response = await api({
                url: 'contract-sign/templates/save-fields',
                data: { template_id: templateId, fields },
            })
            Loading.remove()
            if (!response.data?.success) {
                return Report.failure('Error', response.data?.message || 'Could not save the fields.', 'Okay')
            }
            Report.success('Saved', 'The field layout has been saved.', 'Okay')
            onSaved(fields)
        } catch (error: any) {
            Loading.remove()
            const message = error?.response?.data?.message || 'An error occurred while saving the fields.'
            Report.failure('Error', message, 'Okay')
        }
    }

    const customTextFields = fields.filter((f) => f.type === 'custom_text')
    const signature = hasSignatureField(fields)
    const { needsClaim } = templateRequirements(fields)

    return (
        <section className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderText}>
                    <h4 className={styles.panelTitle}>Edit fields — {templateName}</h4>
                    <p className={styles.panelBody}>
                        Pick a field type, then click on the document to place it. Drag a box to move it, drag the
                        corner handle to resize it, and use the &times; to remove it. Client, address and claim
                        fields are filled from the recipient&apos;s record (or a spreadsheet) at send time; Custom
                        Text is typed per send. Adding a Signature box means the recipient gets a signing link
                        instead of a finished PDF. Claim fields make the template default to one document per claim.
                    </p>
                </div>
                <div className={styles.rowActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                        Back to templates
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={saveFields}>
                        Save field layout
                    </button>
                </div>
            </div>

            <div className={styles.paletteGroups}>
                {PALETTE_GROUPS.map(({ group, label, types }) => (
                    <div key={group} className={styles.paletteGroup}>
                        <span className={styles.paletteGroupLabel}>{label}</span>
                        <div className={styles.palette}>
                            {types.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`${styles.paletteButton} ${type === 'signature' ? styles.signature : ''} ${
                                        activeType === type ? styles.active : ''
                                    }`}
                                    onClick={() => setActiveType(type)}
                                >
                                    {FIELD_TYPE_LABELS[type]}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <p className={styles.placementCount}>
                {fields.length} {fields.length === 1 ? 'field' : 'fields'} placed
                {fields.length > 0 && (signature ? ' — delivered as a signing link' : ' — delivered as a PDF attachment')}
                {needsClaim && ' — stamps claim data (defaults to one document per claim)'}
            </p>

            {customTextFields.length > 0 && (
                <div className={styles.customLabelList}>
                    <p className={styles.miniHeading}>Custom text labels</p>
                    {customTextFields.map((field) => (
                        <div key={field.id} className={styles.field}>
                            <label className={styles.fieldLabel}>
                                Page {field.page} <span className={styles.fieldMeta}>label shown on the Send tab</span>
                            </label>
                            <input
                                className={styles.input}
                                type="text"
                                value={field.label}
                                onChange={(e) => updateLabel(field.id, e.target.value)}
                                placeholder="e.g. Case Reference"
                            />
                        </div>
                    ))}
                </div>
            )}

            {documentFile && (
                <div className={styles.pdfScroller}>
                    <Document
                        file={documentFile}
                        onLoadSuccess={({ numPages: total }: { numPages: number }) => setNumPages(total)}
                        loading={<p className={styles.emptyState}>Loading document...</p>}
                        error={<p className={styles.emptyState}>Could not load this PDF.</p>}
                    >
                        {Array.from({ length: numPages }, (_, index) => {
                            const pageNumber = index + 1
                            const pageFields = fields.filter((f) => f.page === pageNumber)
                            return (
                                <div key={pageNumber} className={styles.pageWrap}>
                                    <span className={styles.pageLabel}>
                                        Page {pageNumber} of {numPages}
                                    </span>
                                    <div className={styles.pageCanvas} ref={registerPageRef(pageNumber)}>
                                        <Page
                                            pageNumber={pageNumber}
                                            width={PAGE_RENDER_WIDTH}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                        <div className={styles.pageOverlay} onClick={(e) => addField(pageNumber, e)}>
                                            {pageFields.map((field) => (
                                                <div
                                                    key={field.id}
                                                    className={`${styles.fieldBox} ${
                                                        field.type === 'signature' ? styles.fieldBoxSignature : ''
                                                    }`}
                                                    style={{
                                                        left: `${field.x * 100}%`,
                                                        top: `${field.y * 100}%`,
                                                        width: `${field.w * 100}%`,
                                                        height: `${field.h * 100}%`,
                                                    }}
                                                    onMouseDown={startDrag(field, 'move')}
                                                >
                                                    <span className={styles.fieldBoxLabel}>{field.label}</span>
                                                    <button
                                                        type="button"
                                                        className={styles.fieldBoxRemove}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            removeField(field.id)
                                                        }}
                                                        aria-label="Remove field"
                                                    >
                                                        &times;
                                                    </button>
                                                    <span
                                                        className={styles.fieldBoxResize}
                                                        onMouseDown={startDrag(field, 'resize')}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </Document>
                </div>
            )}

            <div className={styles.note}>
                <FaInfoCircle />
                <span>
                    Field boxes are saved as page fractions, so the layout stays aligned regardless of the PDF&apos;s
                    page size. Remember to save before leaving the editor.
                </span>
            </div>

            <div className={styles.panelActions}>
                <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className={styles.primaryBtn} onClick={saveFields}>
                    Save field layout
                </button>
            </div>
        </section>
    )
}
